import type {Properties} from "@t/util/Properties";
import {ActionPanelInstance} from "@t/ui/ActionPanel.ts";
import {FormEnvironment} from "@fd/Types.ts";

export type MenuSelect = "" // not selected
	| "target" 				// selection target
	| "path";				// on the path to the selection targe

export type MenuOpen = "open" | "closed" | "always-open";

export interface MenuItem {
	/** REQUIRED unique ID */
	name: string;

    type: string;

    title?: string;
    icon?: string;
    /** Title is safe-html */
    html?: boolean;

    /** If true, this item will not be displayed */
    hidden?: boolean;
	/** True if this item can be hidden and shown after clicking on expansion button (...) */
	collapsed?: boolean;
    /** CSS style attribute */
    style?: string;
    /** CSS class(es) */
    clazz?: string;
    /** WebPreview key */
    preview?: string;

    /** Custom properties (implementation dependent) */
    props?: Properties
}

export interface MenuGroup {
	/** Required unique ID */
	name: string;
	title?: string;
	icon?: string;
	/** CSS class(es) */
	clazz?: string;
	open?: MenuOpen;

	items: MenuItem[];
	action?: TitleAction;
	/** Ordering priority of the group */
	priority?: number;
}

export interface TitleAction {
	icon: string;
	title?: string;	/* displayed as a tooltip */
	onclick?: string | OnClickHandler;
	link?: string;
	target?: string;
}

export interface SideMenu {
	groups: MenuGroup[]
}

export interface SelectableMenuItem extends MenuItem {
	select?: MenuSelect
}

export type Anchor = "left-top" | "right-top" | "left-bottom" | "right-bottom";

export interface MenuItemWithChildren extends SelectableMenuItem {
	/** Prevents being replaced by only-one-visible child */
	keepTitleIfCollapsed?: boolean;

    /** Default is "left-bottom" */
    parentAnchor?: Anchor;
    /** Default is "left-top" */
    childAnchor?: Anchor;
	/** (sub)menu is open */
	open?: boolean,
    children: MenuItem[];
}

/** Root of menu structure */
export interface Menu extends MenuItemWithChildren {
    type: "menu";
	/** Badge text (keep this very short) */
	badge?: string;
	/** Badge icon */
	bicon?: string;
}

export interface SeparatorItem extends MenuItem {
    type: "separator";
}

/**
 * Menu item with similar anatomy as regular nav/action item but
 * this one is displayed just as an active selected place in navigation.
 * E.g. selected mail folder (clicking on it does nothing)
 */
export interface ActiveItem extends MenuItem {
    type: "active";
	/** Badge text (keep this very short) */
	badge?: string;
	/** Badge icon */
	bicon?: string;
}

export type OnClickHandler = (env:FormEnvironment, item:MenuItem) => void;

export interface ActionItem extends SelectableMenuItem {
    /** Badge text (keep this very short) */
    badge?: string;
    /** Badge icon */
    bicon?: string;

    /** Text of confirmation dialog */
    confirm?: string;
    /** Label for YES button of confirmation dialog */
    confirmYes?: string;
    /** Label for NO button of confirmation dialog */
    confirmNo?: string;
	/** Confirmation dialog icon */
	confirmIcon?: string;
	/** Confirmation dialog icon color */
	confirmIconColor?: string;

	/** Format of onclick content (default is script) */
	format?: "script" | "method";
	onclick?: string | OnClickHandler;	/* onclick is optional but mandatory for ExecuteItem */
}

export interface ExecuteItem extends ActionItem {
    type: "action";
    onclick: string | OnClickHandler;
}

export interface NavigateItem extends ActionItem {
    type: "nav";
    /** Route to navigate to */
    link: string;
    target?: string;
}

export interface OptionItem extends MenuItem {
	type: "option"
}

export interface HtmlItem extends MenuItem {
	type: "html",
	body: string;	/* safe-html content */
}

/**
 * This item can display help messages to user.
 * Usually smaller font. The message is in the title property.
 */
export interface HelpItem extends MenuItem {
	type: "help"
}

/**
 * Main app navigation
 */
export interface TopMenu {
	/** Top level menu items */
	sections: MenuItem[];
	/** List of visited pages (helps render navigation ... preserve to hide visited places) */
	visited?: string[];
}

/**
 * Data structure that can be used to change TopMenu data incrementally.
 */
export interface MenuPatch {
	/** Remove all badge texts/icons before applying the patch */
	clearBadges?: boolean;
	/** Remove CSS classes from all items before applying */
	clearClasses?: boolean;
	/** All items will have hidden flag removed */
	showAll?: boolean;
	apply?: MenuItemPatch[];
}

export interface MenuItemPatch {
	/** Unique name of an item which will be patched by this data */
	name: string;

	/** Change title to... */
	title?: string;
	/** Change icon to... */
	icon?: string;
	/** Change title format to... */
	html?: boolean;
	/** Change visibility of the item */
	hidden?: boolean;
	/** Change collapsing ability */
	collapsed?: boolean;

	/** CSS class(es) */
	clazz?: string;

	/** Badge text (keep this very short) */
	badge?: string;
	/** Badge icon */
	bicon?: string;

	/**
	 * Replacement for the link.
	 * You can replace just query part (and keep existing path) if you start the link with "?" (e.g. "?folder=32&account=11")
	 */
	link: string;
}

export function hasChildren(mi: MenuItem):mi is MenuItemWithChildren {
	return Object.hasOwn(mi, "children");
}

export function isAction(mi: MenuItem):mi is ActionItem {
	return mi.type === "action" || mi.type === "nav";
}

export function supportBadges(mi: MenuItem):mi is ActionItem | Menu {
	return mi.type === "action" || mi.type === "nav" || mi.type === "menu";
}

export function isNav(mi: MenuItem):mi is NavigateItem {
	return mi.type === "nav";
}

export function isExecute(mi: MenuItem):mi is ExecuteItem {
	return mi.type === "action";
}

export function isActive(mi: MenuItem):mi is ActiveItem {
	return mi.type === "active";
}

export function isHelp(mi: MenuItem):mi is HelpItem {
	return mi.type === "help";
}

export function isHtml(mi: MenuItem):mi is HtmlItem {
	return mi.type === "html";
}

export function isOption(mi: MenuItem):mi is OptionItem {
	return mi.type === "option";
}

export function traverseMenuChildren(items: MenuItem[], callback: (found: MenuItem) => boolean):boolean {
	if (Array.isArray(items)) {
		for (let mi of items) {
			if (!callback(mi)) return false;
			if (hasChildren(mi)) {
				if (!traverseMenuChildren(mi.children as MenuItem[], callback)) return false;
			}
		}
	}
}

/**
 * Find most recently used navigation item from children of given menu item (or null if not found any)
 */
export function mostRecentNavigationChild(mi:MenuItemWithChildren):NavigateItem|null {
	let maxTimestamp = -1;
	let recent:NavigateItem = null;
	for (let child of mi.children) {
		if (isNav(child)) {
			const ts = getLastUse(child);
			if (ts > maxTimestamp) {
				maxTimestamp = ts;
				recent = child;
			}
		}
	}
	return recent;
}

export function markItemAsRecent(mi:MenuItem):void {
	if (mi.name) {
		sessionStorage.setItem("_menu_item_clicked_"+mi.name, Date.now().toString());
	}
}

export function getLastUse(mi:MenuItem):number {
	if (mi.name) {
		const val = sessionStorage.getItem("_menu_item_clicked_" + mi.name);
		if (val) return parseInt(val);
	}
	return 0;
}

export function traverseMenu(menu: TopMenu, callback: (found: MenuItem) => boolean):void {
	traverseMenuChildren(menu.sections, callback);
}

function patchLink(link:string, patch:string):string {
	if (patch.startsWith("?")) {
		// Query replacement
		const qidx = link.indexOf("?");
		if (qidx >= 0) link = link.substring(0, qidx);
		return link + patch;
	} else return patch;	// full replacement
}

function applyItemPatch(item: MenuItem, patch: MenuItemPatch) {
	if (typeof patch.title === "string") item.title = patch.title;
	if (typeof patch.icon === "string") item.icon = patch.icon;
	if (typeof patch.html === "boolean") item.html = patch.html;
	if (typeof patch.hidden === "boolean") item.hidden = patch.hidden;
	if (typeof patch.collapsed === "boolean") item.collapsed = patch.collapsed;
	if (typeof patch.clazz === "string") item.clazz = patch.clazz;
	if (isAction(item)) {
		if (typeof patch.badge === "string") item.badge = patch.badge;
		if (typeof patch.bicon === "string") item.bicon = patch.bicon;
	}
	if (isNav(item)) {
		if (typeof patch.link === "string") item.link = patchLink(item.link, patch.link);
	}
}

export function applyMenuPatch(menu: TopMenu, patch: MenuPatch) {
	traverseMenu(menu, (mi) => {
		if (patch.clearBadges && isAction(mi)) {
			delete mi.badge;
			delete mi.bicon;
		}
		if (patch.clearClasses) {
			delete mi.clazz;
		}
		if (patch.showAll) {
			delete mi.hidden;
		}
		if (mi.name && Array.isArray(patch.apply)) {
			// has name (patch can be applied)
			let apply:MenuItemPatch = null;
			for (let pt of patch.apply) {
				if (mi.name === pt.name) {
					apply = pt;
					break;
				}
			}
			if (apply != null) {
				applyItemPatch(mi, apply);
			}
		}
		return true;
	});
}

function filterVisibleItems(items: MenuItem[]):MenuItem[] {
	const mi = [];
	if (Array.isArray(items)) {
		for (let it of items) {
			if (it.hidden) continue;
			mi.push(it);
		}
	}
	return mi;
}

export function normalizeTopMenu(menu:TopMenu):TopMenu {
	const n:TopMenu = { sections: [] };
	const visited:string[] = Array.isArray(menu.visited) ? menu.visited : [];
	for (let topItem of menu.sections) {
		if (topItem.hidden) continue;
		if (hasChildren(topItem)) {
			// has children and we have to check how many of them are visible
			const ch = filterVisibleItems(topItem.children);
			if (ch.length === 0) continue;
			if (ch.length === 1) {
				let onlyChild = ch[0];
				if (topItem.keepTitleIfCollapsed) {
					// Only child will get title from original parent
					onlyChild = { ...onlyChild };	// clone
					onlyChild.title = topItem.title;
					onlyChild.html = !!topItem.html;
					onlyChild.icon = topItem.icon;
				}
				n.sections.push(onlyChild);
			} else {
				// Multiple visible items
				let allCollapsed = true;
				for (let chitem of ch) {
					if (!chitem.collapsed || visited.indexOf(chitem.name)) {
						allCollapsed = false;
						break;
					}
				}
				if (allCollapsed) topItem.collapsed = true;   // if all visible children are collapsed, then parent is also collapsed
				topItem.children = ch;
				n.sections.push(topItem);           // keep structure as is
			}
		} else {
			n.sections.push(topItem);
		}
	}
	return n;
}

export type ChangeOperation = "append" | "prepend" | "insert_before" | "insert_after" | "remove";

export function findGroupPosition(m:SideMenu, name:string):number {
	return m.groups.findIndex((mg) => mg.name === name);
}

export interface MenuItemPosition {
	list:MenuItem[];
	index:number;
}

function findPosition(list:MenuItem[], name:string):MenuItemPosition|null {
	for (let i = 0; i < list.length; i++) {
		const mi = list[i];
		if (mi.name === name) return { list, index:i };
		if (hasChildren(mi)) {
			const pos = findPosition(mi.children, name);
			if (pos) return pos;
		}
	}
	return null;
}

export function findItemPosition(m:SideMenu, name:string):MenuItemPosition {
	for (let mg of m.groups) {
		if (Array.isArray(mg.items)) {
			const pos = findPosition(mg.items, name);
			if (pos) return pos;
		}
	}
	return null;
}


export interface MenuChange {
	op: ChangeOperation;
	ref?: string;
	group?: MenuGroup;
	item?: MenuItem;
	remove?: "group" | "item";	/* only for remove operation to decide what's being removed */
}

export interface SideMenuChanges {
	pages: string[];
	changes: MenuChange[];

	/** Backend is capable of including actual action panels to save a network call */
	panels?: ActionPanelInstance<any>[];
}


/********* STANDARD MENU ITEMS *********/

export const NEW_RECS_GROUP_ID:string = "menu_new";
export const VIEWS_GROUP_ID:string = "menu_views";
export const TOOLS_GROUP_ID:string = "menu_tools";
export const PRINTS_GROUP_ID:string = "menu_prints";

export const NEW_RECS_GROUP:Readonly<MenuGroup> = {
	name: NEW_RECS_GROUP_ID,
	title: "menu.title.newRecord",
	items: [],
	priority: 5
};

export const VIEWS_GROUP:Readonly<MenuGroup> = {
	name: VIEWS_GROUP_ID,
	title: "menu.title.views",
	items: [],
	priority: 10
};

export const TOOLS_GROUP:Readonly<MenuGroup> = {
	name: TOOLS_GROUP_ID,
	title: "menu.title.tools",
	items: [],
	priority: 20
};

export const PRINTS_GROUP:Readonly<MenuGroup> = {
	name: PRINTS_GROUP_ID,
	title: "menu.title.prints",
	items: [],
	priority: 30
};

export const KNOWN_GROUPS:Readonly<MenuGroup[]> = [NEW_RECS_GROUP, VIEWS_GROUP, TOOLS_GROUP, PRINTS_GROUP];